<script lang="ts" setup generic="T">
import Select, { /* type SelectEmits, */ type SelectProps, type SelectSlots } from 'primevue/Select'

interface Props
  extends /* @vue-ignore */ Omit<SelectProps, 'options' | 'filter' | 'fluid' | 'size'> {
  error?: boolean
  options?: T[]
  optionLabel?: string | ((data: unknown) => string)
  optionValue?: string | ((data: unknown) => string)
  disabled?: boolean
  focusOnShow?: boolean
}

//interface Emits extends /* @vue-ignore */ SelectEmits {}

const props = withDefaults(defineProps<Props>(), {
  options: undefined,
  optionLabel: undefined,
  optionValue: undefined,
  disabled: false,
  focusOnShow: true,
})

const emit = defineEmits(['change', 'show', 'hide', 'focus', 'blur'])

const model = defineModel<Props['modelValue'] | null>()

const errorInjected = inject<Ref<boolean>>('error', ref(false))

const slots =
  defineSlots<Omit<Record<keyof SelectSlots, SelectSlots[keyof SelectSlots] | object>, 'option'>>()

const filteredSlots = computed(() => {
  const {
    value,
    header,
    footer,
    optiongroup,
    emptyfilter,
    empty,
    content,
    loader,
    clearicon,
    dropdownicon,
    loadingicon,
    filtericon,
  } = slots

  return {
    value,
    header,
    footer,
    optiongroup,
    emptyfilter,
    empty,
    content,
    loader,
    clearicon,
    dropdownicon,
    loadingicon,
    filtericon,
  }
})

const elementRef = useTemplateRef('elementRef')

const focus = useFocus()

const displayOption = (label?: string) => {
  if (!label) return '...'
  const trimed = label.substring(0, 30)
  return label.length > 30 ? `${trimed}...` : trimed
}

const filter = computed(() => (props.options?.length ?? 0) > 15)
</script>

<template>
  <div ref="elementRef">
    <Select
      v-bind="$props"
      v-model="model"
      fluid
      :filter="filter"
      :auto-filter-focus="filter"
      :invalid="errorInjected || error"
      placeholder="Seleccione"
      filter-match-mode="contains"
      :virtual-scroller-options="{ itemSize: 20 }"
      :size="$viewport.isLessThan('md') ? 'small' : undefined"
      @before-show="() => (focusOnShow ? focus({ target: elementRef, lessThan: 'lg' }) : undefined)"
      @change="emit('change')"
      @show="emit('show')"
      @hide="emit('hide')"
      @focus="emit('focus')"
      @blur="emit('blur')"
    >
      <template
        v-for="(_, slot) in filteredSlots"
        #[slot]="slotData"
      >
        <slot
          :name="slot"
          v-bind="slotData"
        />
      </template>
      <template
        v-if="optionLabel"
        #option="slotProps"
      >
        {{
          displayOption(
            slotProps?.option?.[
              typeof optionLabel === 'function' ? optionLabel({}) : (optionLabel ?? '_')
            ]
          ).toUpperCase()
        }}
      </template>
    </Select>
  </div>
</template>
